import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { faBell, faCheckDouble, faCircleInfo, faClose } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { EPlatformNoticeCategoryType } from "../../Enum/EPlatformNoticeCategoryType"
import { EPlatformNoticePriorityType } from "../../Enum/EPlatformNoticePriorityType"
import { ERequestType } from "../../Enum/ERequestType"
import { usePlatformNotices } from "../../hooks/PlatformNotice/usePlatformNotices"
import { PlatformNotice } from "../../Objects/PlatformNotice/PlatformNotice"
import Animate, { AnimationTypes } from "../Animate/Animate"
import ModalPortal from "../Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"

const POLLING_MS = 180000

const formatDateTime = (date: Date) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)

const priorityClassMap: Record<EPlatformNoticePriorityType, string> = {
  [EPlatformNoticePriorityType.Low]: "border-slate-200 bg-slate-50 text-slate-700",
  [EPlatformNoticePriorityType.Normal]: "border-sky-200 bg-sky-50 text-sky-700",
  [EPlatformNoticePriorityType.High]: "border-amber-200 bg-amber-50 text-amber-700",
  [EPlatformNoticePriorityType.Critical]: "border-red-200 bg-red-50 text-red-700",
}

const priorityRankMap: Record<EPlatformNoticePriorityType, number> = {
  [EPlatformNoticePriorityType.Low]: 0,
  [EPlatformNoticePriorityType.Normal]: 1,
  [EPlatformNoticePriorityType.High]: 2,
  [EPlatformNoticePriorityType.Critical]: 3,
}

const categoryClassMap: Record<EPlatformNoticeCategoryType, string> = {
  [EPlatformNoticeCategoryType.General]: "bg-slate-100 text-slate-700",
  [EPlatformNoticeCategoryType.Release]: "bg-emerald-100 text-emerald-700",
  [EPlatformNoticeCategoryType.Incident]: "bg-red-100 text-red-700",
  [EPlatformNoticeCategoryType.Maintenance]: "bg-amber-100 text-amber-700",
  [EPlatformNoticeCategoryType.Announcement]: "bg-blue-100 text-blue-700",
}

const PANEL_WIDTH = 420
const PANEL_MARGIN = 8

interface PlatformNoticeBellProps {
  requestType?: ERequestType
}

export const PlatformNoticeBell: React.FC<PlatformNoticeBellProps> = ({ requestType = ERequestType.Restaurant }) => {
  const { t } = useTranslation("platformNotice")

  const { getInbox, getUnreadCount, markAsRead, markAllAsRead } = usePlatformNotices()

  const [isOpen, setIsOpen] = useState(false)

  const [notices, setNotices] = useState<PlatformNotice[]>([])

  const [selectedNotice, setSelectedNotice] = useState<PlatformNotice | null>(null)

  const [unreadCount, setUnreadCount] = useState(0)

  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const panelRef = useRef<HTMLDivElement | null>(null)

  const [panelPosition, setPanelPosition] = useState<{
    top?: number
    right: number
    bottom?: number
  }>({ top: 0, right: PANEL_MARGIN })

  const visibleUnreadCount = unreadCount > 99 ? "99+" : `${unreadCount}`

  const orderedNotices = useMemo(
    () =>
      [...notices].sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1
        if (a.priorityType !== b.priorityType) return priorityRankMap[b.priorityType] - priorityRankMap[a.priorityType]
        return b.publishAtUtc.getTime() - a.publishAtUtc.getTime()
      }),
    [notices],
  )

  const refreshCount = () => getUnreadCount(setUnreadCount, requestType)

  const refreshInbox = () => {
    getInbox(
      (data) => {
        setNotices(data)
        setUnreadCount(data.filter((item) => !item.isRead).length)
      },
      true,
      requestType,
    )
  }

  const updatePanelPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()

    if (!rect) return

    const availableWidth = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN * 2)
    const left = Math.min(
      Math.max(PANEL_MARGIN, rect.right - availableWidth),
      window.innerWidth - availableWidth - PANEL_MARGIN,
    )

    const basePosition = {
      right: window.innerWidth - left - availableWidth,
    }

    if (rect.top > window.innerHeight / 2) {
      setPanelPosition({
        ...basePosition,
        bottom: window.innerHeight - rect.top + PANEL_MARGIN,
      })

      return
    }

    setPanelPosition({
      ...basePosition,
      top: rect.bottom + PANEL_MARGIN,
    })
  }, [])

  useEffect(() => {
    refreshCount()

    const intervalId = window.setInterval(refreshCount, POLLING_MS)

    return () => window.clearInterval(intervalId)
  }, [getUnreadCount, requestType])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return
      }

      setIsOpen(false)
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    updatePanelPosition()

    window.addEventListener("resize", updatePanelPosition)
    window.addEventListener("scroll", updatePanelPosition, true)

    return () => {
      window.removeEventListener("resize", updatePanelPosition)
      window.removeEventListener("scroll", updatePanelPosition, true)
    }
  }, [isOpen, updatePanelPosition])

  const handleOpen = () => {
    const nextOpen = !isOpen
    setIsOpen(nextOpen)

    if (nextOpen) {
      updatePanelPosition()
      refreshInbox()
    }
  }

  const handleSelectNotice = (notice: PlatformNotice) => {
    setSelectedNotice(notice)

    if (notice.isRead) {
      return
    }

    markAsRead(
      notice.id,
      () => {
        setNotices((current) =>
          current.map((item) => (item.id === notice.id ? new PlatformNotice({ ...item, isRead: true }) : item)),
        )
        setUnreadCount((current) => Math.max(0, current - 1))
      },
      requestType,
    )
  }

  const handleMarkAll = () => {
    markAllAsRead(() => {
      setNotices((current) => current.map((item) => new PlatformNotice({ ...item, isRead: true })))
      setUnreadCount(0)
    }, requestType)
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        aria-label={t("bell_open")}
        title={t("bell_open")}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border-2 border-orange-300 bg-white text-slate-700 shadow-sm transition hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700"
        data-interaction-description-key="interactionDescriptions:components.platformNotice.bell.button.open"
      >
        <FontAwesomeIcon icon={faBell} />

        {unreadCount > 0 && (
          <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white ring-2 ring-white">
            {visibleUnreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <ModalPortal>
          <div
            ref={panelRef}
            className="fixed z-[950] w-[min(92vw,420px)] overflow-hidden rounded-lg border-2 border-orange-300 bg-white shadow-2xl shadow-orange-900/20"
            style={{ top: panelPosition.top, right: panelPosition.right, bottom: panelPosition.bottom }}
          >
            <div className="flex items-start justify-between gap-3 border-b border-orange-100 bg-orange-50/60 p-4">
              <div>
                <h2 className="text-sm font-black text-slate-900">{t("inbox_title")}</h2>

                <p className="text-xs text-slate-500">{t("inbox_subtitle", { count: unreadCount })}</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMarkAll}
                  disabled={unreadCount === 0}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-emerald-700 bg-emerald-600 text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:border-emerald-200 disabled:bg-emerald-100 disabled:text-emerald-400"
                  aria-label={t("mark_all_read")}
                  title={t("mark_all_read")}
                  data-interaction-description-key="interactionDescriptions:components.platformNotice.bell.button.markAll"
                >
                  <FontAwesomeIcon icon={faCheckDouble} />
                </button>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-red-700 bg-red-600 text-white shadow-sm transition hover:bg-red-700"
                  aria-label={t("close")}
                  title={t("close")}
                  data-interaction-description-key="interactionDescriptions:components.platformNotice.bell.button.close"
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {orderedNotices.length === 0 ? (
                <div className="flex flex-col items-center gap-2 p-8 text-center text-slate-500">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-2xl text-slate-400" />

                  <p className="text-sm font-semibold">{t("empty")}</p>
                </div>
              ) : (
                orderedNotices.map((notice) => (
                  <button
                    key={notice.id}
                    type="button"
                    onClick={() => handleSelectNotice(notice)}
                    className={`w-full border-b border-orange-100 p-4 text-left transition hover:bg-orange-50 ${
                      notice.isRead ? "bg-white" : "bg-orange-50/70"
                    }`}
                    data-interaction-description-key="interactionDescriptions:components.platformNotice.bell.button.notice"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {!notice.isRead && <span className="h-2 w-2 rounded-full bg-red-600" />}

                          <span
                            className={`rounded px-2 py-0.5 text-[10px] font-black ${categoryClassMap[notice.categoryType]}`}
                          >
                            {t(`category.${notice.categoryType}`)}
                          </span>

                          <span
                            className={`rounded border px-2 py-0.5 text-[10px] font-black ${priorityClassMap[notice.priorityType]}`}
                          >
                            {t(`priority.${notice.priorityType}`)}
                          </span>
                        </div>

                        <h3 className="mt-2 line-clamp-2 text-sm font-black text-slate-900">{notice.title}</h3>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-600">{notice.message}</p>
                      </div>

                      <span className="shrink-0 text-[10px] font-bold text-slate-400">
                        {formatDateTime(notice.publishAtUtc)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </ModalPortal>
      )}

      {selectedNotice && (
        <ModalPortal>
          <div className={defaultStyle.overlay}>
            <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
              <section className="flex max-h-[88vh] w-[min(94vw,760px)] flex-col overflow-hidden rounded-lg border-2 border-orange-300 bg-white shadow-2xl shadow-orange-950/25">
                <div className="shrink-0 flex items-start justify-between gap-4 border-b border-orange-100 p-5">
                  <div className="min-w-0">
                    <h2 className="break-words text-lg font-black leading-snug text-slate-950">
                      {selectedNotice.title}
                    </h2>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedNotice(null)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-red-700 bg-red-600 text-white shadow-sm transition hover:bg-red-700"
                    aria-label={t("close")}
                    title={t("close")}
                    data-interaction-description-key="interactionDescriptions:components.platformNotice.bell.button.closeDetails"
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </button>
                </div>

                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-scroll whitespace-pre-wrap break-words p-5 text-sm leading-7 text-slate-800 [overflow-wrap:anywhere]">
                  {selectedNotice.message}
                </div>

                <div className="shrink-0 grid gap-2 border-t border-orange-100 bg-orange-50/60 p-4 sm:grid-cols-3">
                  <div className="rounded-md border border-orange-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase text-slate-500">{t("field_category")}</p>

                    <p className="mt-1 text-sm font-black text-slate-800">
                      {t(`category.${selectedNotice.categoryType}`)}
                    </p>
                  </div>

                  <div className="rounded-md border border-orange-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase text-slate-500">{t("field_priority")}</p>

                    <p className="mt-1 text-sm font-black text-slate-800">
                      {t(`priority.${selectedNotice.priorityType}`)}
                    </p>
                  </div>

                  <div className="rounded-md border border-orange-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-black uppercase text-slate-500">{t("field_publish_date")}</p>

                    <p className="mt-1 text-sm font-black text-slate-800">
                      {formatDateTime(selectedNotice.publishAtUtc)}
                    </p>
                  </div>
                </div>
              </section>
            </Animate>
          </div>
        </ModalPortal>
      )}
    </div>
  )
}
